import { create } from "zustand";
import type { Battle, BattleType, BattleLogEvent, PlayerItem, AutoBattleMode } from "../types";
import { battleApi } from "../api/battle";
import { enemyHitBatchSize } from "../hooks/useBattleReplay";
import { useGameStore } from "./useGameStore";

type BattlePhase = "idle" | "starting" | "animating" | "results" | "claimed";

/** Minimum time on the arena before the result overlay (pre-simulated fights feel instant otherwise). */
const MIN_REPLAY_MS = 1800;

interface BattleState {
  battle: Battle | null;
  phase: BattlePhase;
  visibleLog: BattleLogEvent[];
  replayIndex: number;
  animatingStartedAt: number | null;
  error: string | null;
  droppedItems: PlayerItem[];
  duelInfoMessages: string[];

  startBattle: (type?: BattleType, stageId?: number, autoBattleMode?: AutoBattleMode) => Promise<void>;
  loadBattle: (battleId: number, infoMessages?: string[]) => Promise<boolean>;
  claimRewards: () => Promise<PlayerItem[]>;
  finalizePreclaimedBattle: () => void;
  replayNextEvent: () => void;
  reset: () => void;
}

export const useBattleStore = create<BattleState>((set, get) => ({
  battle: null,
  phase: "idle",
  visibleLog: [],
  replayIndex: 0,
  animatingStartedAt: null,
  error: null,
  droppedItems: [],
  duelInfoMessages: [],

  startBattle: async (type: BattleType = "PVE", stageId?: number, autoBattleMode?: AutoBattleMode) => {
    useGameStore.getState().captureHeroSnapshot();
    set({
      phase: "starting",
      error: null,
      battle: null,
      visibleLog: [],
      replayIndex: 0,
      animatingStartedAt: null,
      droppedItems: [],
      duelInfoMessages: [],
    });
    try {
      const battle = await battleApi.start(type, stageId, autoBattleMode);
      const firstLog = battle.log && battle.log.length > 0 ? battle.log.slice(0, 1) : [];
      set({
        battle,
        phase: "animating",
        replayIndex: firstLog.length,
        visibleLog: firstLog,
        animatingStartedAt: Date.now(),
      });
      if (battle.info_messages?.length) {
        useGameStore.getState().pushInfoMessages(battle.info_messages);
      }
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to start battle", phase: "idle" });
    }
  },

  loadBattle: async (battleId: number, infoMessages?: string[]) => {
    useGameStore.getState().captureHeroSnapshot();
    set({
      phase: "starting",
      error: null,
      battle: null,
      visibleLog: [],
      replayIndex: 0,
      animatingStartedAt: null,
      droppedItems: [],
      duelInfoMessages: infoMessages ?? [],
    });
    try {
      const battle = await battleApi.get(battleId);
      const firstLog = battle.log && battle.log.length > 0 ? battle.log.slice(0, 1) : [];
      set({
        battle,
        phase: "animating",
        replayIndex: firstLog.length,
        visibleLog: firstLog,
        animatingStartedAt: Date.now(),
      });
      return true;
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to load battle", phase: "idle" });
      return false;
    }
  },

  replayNextEvent: () => {
    const { battle, replayIndex, animatingStartedAt } = get();
    if (!battle?.log) return;

    if (replayIndex < battle.log.length) {
      const batch = enemyHitBatchSize(battle.log, replayIndex);
      const nextIndex = Math.min(replayIndex + batch, battle.log.length);
      const nextLog = battle.log.slice(0, nextIndex);
      set({ visibleLog: nextLog, replayIndex: nextIndex });
      return;
    }

    const elapsed = Date.now() - (animatingStartedAt ?? 0);
    if (elapsed < MIN_REPLAY_MS) return;

    set({ phase: "results" });
    if (battle.status !== "CLAIMED") {
      void get().claimRewards();
    }
  },

  /** Push deferred info lines after the player dismisses a pre-claimed battle overlay. */
  finalizePreclaimedBattle: () => {
    const { battle, duelInfoMessages } = get();
    if (!battle) return;
    useGameStore.getState().pushInfoMessages([
      ...duelInfoMessages,
      ...(battle.info_messages ?? []),
    ]);
    set({ duelInfoMessages: [] });
    void useGameStore.getState().refreshHero();
    void useGameStore.getState().refreshBalance();
  },

  claimRewards: async () => {
    const { battle, phase, duelInfoMessages } = get();
    if (!battle || phase === "claimed") return [];
    try {
      const res = await battleApi.claim(battle.id);
      useGameStore.getState().pushInfoMessages([
        ...duelInfoMessages,
        ...(res.info_messages ?? []),
      ]);
      set({ phase: "claimed", droppedItems: res.dropped_items ?? [], duelInfoMessages: [] });
      return res.dropped_items ?? [];
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to claim rewards" });
      return [];
    }
  },

  reset: () =>
    set({
      battle: null,
      phase: "idle",
      visibleLog: [],
      replayIndex: 0,
      animatingStartedAt: null,
      error: null,
      droppedItems: [],
      duelInfoMessages: [],
    }),
}));
